import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { useToast } from '../../../hooks/use-toast';
import { QrCode, Plus, Trash2, Download, ExternalLink, Pencil, X, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRPagesAdmin = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    title: '',
    slug: '',
    page_headline: 'Enter Our Weekly Drawing!',
    page_subtext: 'Win free ice cream & exclusive offers!',
    flyer_headline_1: 'Enter Our Weekly',
    flyer_headline_2: 'Ice Cream Giveaway Drawing!',
    flyer_subtext: 'Absolutely FREE to Enter!',
    flyer_scan_text: 'SCAN QR Code Below',
    flyer_tagline: 'Winners announced weekly! Good luck!',
    is_active: true,
  };

  const [form, setForm] = useState({ ...emptyForm });

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/qr-flyer/admin/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPages(await res.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to load QR pages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  const autoSlug = (title) => {
    return title.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast({ title: 'Error', description: 'Title is required', variant: 'destructive' }); return; }
    if (!form.slug.trim()) { toast({ title: 'Error', description: 'Slug is required', variant: 'destructive' }); return; }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      let res;
      if (editing) {
        res = await fetch(`${API_URL}/api/qr-flyer/admin/pages/${editing}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      } else {
        res = await fetch(`${API_URL}/api/qr-flyer/admin/pages`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      }

      if (res.ok) {
        toast({ title: editing ? 'Page Updated' : 'Page Created', description: `QR page "${form.title}" saved.` });
        setEditing(null);
        setCreating(false);
        setForm({ ...emptyForm });
        fetchPages();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to save', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to save page', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/qr-flyer/admin/pages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Deleted', description: `"${title}" removed.` });
        fetchPages();
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const startEdit = (page) => {
    setEditing(page.id);
    setCreating(false);
    setForm({
      title: page.title || '',
      slug: page.slug || '',
      page_headline: page.page_headline || emptyForm.page_headline,
      page_subtext: page.page_subtext || emptyForm.page_subtext,
      flyer_headline_1: page.flyer_headline_1 || emptyForm.flyer_headline_1,
      flyer_headline_2: page.flyer_headline_2 || emptyForm.flyer_headline_2,
      flyer_subtext: page.flyer_subtext || emptyForm.flyer_subtext,
      flyer_scan_text: page.flyer_scan_text || emptyForm.flyer_scan_text,
      flyer_tagline: page.flyer_tagline || emptyForm.flyer_tagline,
      is_active: page.is_active !== false,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setCreating(false);
    setForm({ ...emptyForm });
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast({ title: 'Copied', description: 'URL copied to clipboard' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">QR Landing Pages</h2>
          <p className="text-gray-500">Create custom signup pages, each with its own printable QR flyer</p>
        </div>
        {!creating && !editing && (
          <Button onClick={() => { setCreating(true); setForm({ ...emptyForm }); }} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="create-qr-page-btn">
            <Plus className="w-4 h-4 mr-2" /> New Page
          </Button>
        )}
      </div>

      {/* Default /enter flyer */}
      <Card className="border-pink-200 bg-pink-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <QrCode className="w-6 h-6 text-pink-600" />
              <div>
                <p className="font-semibold text-gray-900">Default Drawing Page</p>
                <p className="text-sm text-gray-500">1staveicp.com/enter — Always active</p>
              </div>
            </div>
            <Button onClick={() => window.open(`${API_URL}/api/qr-flyer/download`, '_blank')} variant="outline" className="border-pink-400 text-pink-600 hover:bg-pink-50" data-testid="download-default-flyer-btn">
              <Download className="w-4 h-4 mr-2" /> Download Flyer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit Form */}
      {(creating || editing) && (
        <Card className="border-blue-300">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{editing ? 'Edit Page' : 'Create New Page'}</span>
              <Button variant="ghost" size="sm" onClick={cancelEdit}><X className="w-4 h-4" /></Button>
            </CardTitle>
            <CardDescription>Configure both the landing page text and the printed flyer text</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Page Title (admin label)</Label>
                <Input value={form.title} data-testid="qr-form-title" onChange={(e) => { setForm({ ...form, title: e.target.value, slug: creating ? autoSlug(e.target.value) : form.slug }); }} className="mt-1" placeholder="e.g. Holiday Special" />
              </div>
              <div>
                <Label>URL Slug</Label>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-gray-500 mr-1">1staveicp.com/p/</span>
                  <Input value={form.slug} data-testid="qr-form-slug" onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="holiday-special" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} />
              <Label>{form.is_active ? 'Active' : 'Inactive'}</Label>
            </div>

            {/* Landing Page Text */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3 text-gray-700">Landing Page Text (what visitors see)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Headline</Label>
                  <Input value={form.page_headline} data-testid="qr-form-page-headline" onChange={(e) => setForm({ ...form, page_headline: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Subtext</Label>
                  <Input value={form.page_subtext} data-testid="qr-form-page-subtext" onChange={(e) => setForm({ ...form, page_subtext: e.target.value })} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Flyer Text */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3 text-gray-700">Printed Flyer Text (what appears on the PDF)</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Headline Line 1</Label>
                    <Input value={form.flyer_headline_1} onChange={(e) => setForm({ ...form, flyer_headline_1: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Headline Line 2</Label>
                    <Input value={form.flyer_headline_2} onChange={(e) => setForm({ ...form, flyer_headline_2: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Big Subtext (pink)</Label>
                    <Input value={form.flyer_subtext} onChange={(e) => setForm({ ...form, flyer_subtext: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Scan Instruction</Label>
                    <Input value={form.flyer_scan_text} onChange={(e) => setForm({ ...form, flyer_scan_text: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Bottom Tagline</Label>
                    <Input value={form.flyer_tagline} onChange={(e) => setForm({ ...form, flyer_tagline: e.target.value })} className="mt-1" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="qr-form-save-btn">
                {saving ? 'Saving...' : editing ? 'Update Page' : 'Create Page'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Pages List */}
      {pages.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700">Custom Pages ({pages.length})</h3>
          {pages.map((page) => (
            <Card key={page.id} className={`${page.is_active ? '' : 'opacity-60'}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{page.title}</p>
                        {!page.is_active && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>1staveicp.com/p/{page.slug}</span>
                        <button onClick={() => copyUrl(`https://1staveicp.com/p/${page.slug}`)} className="hover:text-pink-600" title="Copy URL">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => window.open(`${API_URL}/api/qr-flyer/download/${page.slug}`, '_blank')} title="Download Flyer PDF" data-testid={`download-flyer-${page.slug}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(`/p/${page.slug}`, '_blank')} title="Preview Page">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => startEdit(page)} title="Edit" data-testid={`edit-page-${page.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(page.id, page.title)} className="text-red-500 hover:text-red-700 hover:border-red-300" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {pages.length === 0 && !creating && (
        <div className="text-center py-12 text-gray-400">
          <QrCode className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No custom pages yet. Click "New Page" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default QRPagesAdmin;
